import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { adminNotificationService } from "../Api/Services/adminNotificationService";
import { createOrderHubConnection } from "../Api/orderHub";
import { AdminNotification } from "../Models/AdminNotification";

const LATEST_PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 60000;

type AdminNotificationContextType = {
  unreadCount: number;
  latest: AdminNotification[];
  // Bumped whenever notifications change, so lists can refetch their own page
  version: number;
  refresh: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
};

const AdminNotificationContext = createContext<AdminNotificationContextType>({
  unreadCount: 0,
  latest: [],
  version: 0,
  refresh: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {}
});

export const AdminNotificationProvider = ({ children }: { children: ReactNode }) => {
  const { isAdmin, isOperator } = useAuth();
  const isStaff = isAdmin || isOperator;

  const [unreadCount, setUnreadCount] = useState(0);
  const [latest, setLatest] = useState<AdminNotification[]>([]);
  const [version, setVersion] = useState(0);
  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [count, page] = await Promise.all([
        adminNotificationService.getUnreadCount(),
        adminNotificationService.getAll({ page: 1, pageSize: LATEST_PAGE_SIZE })
      ]);
      setUnreadCount(count);
      setLatest(page.items);
      setVersion((v) => v + 1);
    } catch {
      // Keep the last known state; the next poll or event retries
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    setLatest((items) => items.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    await adminNotificationService.markAsRead(id);
    await refresh();
  }, [refresh]);

  const markAllAsRead = useCallback(async () => {
    setUnreadCount(0);
    setLatest((items) => items.map((n) => ({ ...n, isRead: true })));
    await adminNotificationService.markAllAsRead();
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (!isStaff) {
      setUnreadCount(0);
      setLatest([]);
      return;
    }

    void refresh();

    const hub = createOrderHubConnection();
    hubRef.current = hub;
    hub.on("AdminNotificationCreated", () => {
      void refresh();
    });
    hub.start().catch(() => {
      // Live updates are a bonus; polling below still keeps the count fresh
    });

    const poll = setInterval(() => {
      if (!document.hidden) {
        void refresh();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(poll);
      hub.stop();
      hubRef.current = null;
    };
  }, [isStaff, refresh]);

  return (
    <AdminNotificationContext.Provider value={{ unreadCount, latest, version, refresh, markAsRead, markAllAsRead }}>
      {children}
    </AdminNotificationContext.Provider>
  );
};

export const useAdminNotifications = () => useContext(AdminNotificationContext);
